import React from 'react';
import { Popover, Table, Tooltip } from 'antd';
import styled from 'styled-components';
import { LinkOutlined } from '@ant-design/icons';
import { IPipelineRun } from '../../../core/modules/pipelines/pipelines.model';
import StatusBadge from './StatusBadge';
import DurationCell from './DurationCell';
import { formatTimestampShort } from '../utils/formatStage';

const StyledTable = styled(Table)`
  .failed-row {
    background: #fff1f0;
  }

  .ant-table-thead > tr > th {
    background: #fafafa;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.55);
  }

  .ant-table-tbody > tr > td {
    font-size: 13px;
  }
`;

const StudioLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
`;

const RunIdLabel = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
`;

const ErrorCell = styled.span`
  color: #cf1322;
`;

const WarnCell = styled.span`
  color: #d48806;
`;

const ErrorBody = styled.div`
  width: min(560px, calc(100vw - 80px));
  max-height: min(420px, 60vh);
  overflow-y: auto;
  overflow-x: hidden;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  padding-right: 4px;
`;

const ErrorMeta = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
`;

const truncateId = (id: string) => {
  if (id.length <= 14) {
    return id;
  }

  return `${id.slice(0, 6)}…${id.slice(-4)}`;
};

interface IRunHistoryTableProps {
  runs: IPipelineRun[];
  showActivityColumn?: boolean;
  showDurationColumn?: boolean;
  showErrorColumn?: boolean;
}

const rowKeyFor = (run: IPipelineRun) =>
  `${run.runId || 'no-op'}::${run.activityName ?? 'parent'}::${run.startedAt ?? ''}`;

const localTimeZoneAbbrev = (() => {
  const formatted = new Date()
    .toLocaleTimeString(undefined, { timeZoneName: 'short' });
  const parts = formatted.split(' ');

  return parts[parts.length - 1];
})();

const StartedCell = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.75);
`;

/** Bounded + scrollable Popover; Tooltip overlay can't be scrolled. */
const ErrorWarnCell: React.FC<{ run: IPipelineRun }> = ({ run }) => {
  const isFailed = run.status === 'failed';
  const message = run.errorMessage || run.warnMessage;
  const repeats = run.repeatCount ?? 1;
  const warnCount = run.warnCount ?? 0;

  if (!message && warnCount === 0) {
    return <>—</>;
  }

  const text = message || `${warnCount} logged exception${warnCount === 1 ? '' : 's'}`;
  const Cell = isFailed ? ErrorCell : WarnCell;

  const content = (
    <>
      <ErrorBody>{text}</ErrorBody>
      {(repeats > 1 || warnCount > 1) && (
        <ErrorMeta>
          {repeats > 1 && (
            <div>
              Repeated {repeats}× since{' '}
              {run.repeatSinceAt ? formatTimestampShort(run.repeatSinceAt) : 'earlier'}
            </div>
          )}
          {warnCount > 1 && (
            <div>
              +{warnCount - 1} more exception{warnCount - 1 === 1 ? '' : 's'} in this run
            </div>
          )}
        </ErrorMeta>
      )}
    </>
  );

  return (
    <Popover
      content={content}
      trigger='hover'
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.25}
      placement='topLeft'
      overlayStyle={{ maxWidth: 'none' }}
    >
      <Cell>
        {text}
        {repeats > 1 && (
          <span style={{ opacity: 0.65, marginLeft: 6 }}>×{repeats}</span>
        )}
      </Cell>
    </Popover>
  );
};

const RunHistoryTable: React.FC<IRunHistoryTableProps> = ({
  runs,
  showActivityColumn = true,
  showDurationColumn = true,
  showErrorColumn = true
}) => {
  const showActivity = showActivityColumn && (
    runs.some((r) => r.activityName) ||
    runs.some((r) => (r.children?.length ?? 0) > 0)
  );

  const columns = [
    ...(showActivity ? [{
      title: 'Activity',
      dataIndex: 'activityName',
      key: 'activityName',
      width: 220,
      render: (v: string) => v
        ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
        : '—'
    }] : [{
      title: 'ID',
      dataIndex: 'runId',
      key: 'runId',
      width: 150,
      render: (id: string, run: IPipelineRun) => (
        <Tooltip title={id || run.invocationId || '—'}>
          {run.studioUrl
            ? <StudioLink href={run.studioUrl} target='_blank' rel='noreferrer'>
              <LinkOutlined />
              {truncateId(id || run.invocationId || '')}
            </StudioLink>
            : <RunIdLabel>{truncateId(id || run.invocationId || '—')}</RunIdLabel>}
        </Tooltip>
      )
    }]),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: IPipelineRun['status']) => <StatusBadge status={s} />
    },
    {
      title: `Started (${localTimeZoneAbbrev})`,
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 200,
      render: (v: string) => <StartedCell>{formatTimestampShort(v)}</StartedCell>
    },
    ...(showDurationColumn ? [{
      title: 'Duration',
      dataIndex: 'durationSec',
      key: 'durationSec',
      width: 90,
      render: (s: number) => <DurationCell seconds={s} />
    }] : []),
    ...(showErrorColumn ? [{
      title: 'Error / Warning',
      key: 'errorOrWarn',
      ellipsis: true,
      render: (_: unknown, run: IPipelineRun) => <ErrorWarnCell run={run} />
    }] : [])
  ];

  return (
    <StyledTable
      rowKey={rowKeyFor as any}
      size='small'
      pagination={false}
      columns={columns as any}
      dataSource={runs}
      expandable={{ childrenColumnName: 'children' }}
      rowClassName={(record: any) => (record.status === 'failed' ? 'failed-row' : '')}
    />
  );
};

export default RunHistoryTable;
